<template>
    <v-row
        no-gutters
        class="pr-8 pl-6 pt-8"
    >
        <v-col cols="12">
            <!-- PRODUCTS -->
            <v-row no-gutters class="pl-4">
                <v-col cols="12">
                    <BillingDialogContentHeader
                        :text-plurar="productTableData.text_plural"
                        :text-singular="productTableData.text_singular"
                        :count="productTableData.items.value.length"
                    >
                        <template slot="left-button">
                            <div class="d-inline ml-3">
                                <action-button @click="onAddProductClick">
                                    {{ $t('Add') }}
                                </action-button>
                            </div>
                        </template>

                        <template slot="right-button">
                            <div class="d-inline">
                                <action-button-secondary
                                    @click="onImportProductClick"
                                    :loading="import_loading"
                                    icon="mdi-download">
                                    {{ $t('Import classificators') }}
                                </action-button-secondary>
                            </div>
                        </template>
                    </BillingDialogContentHeader>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12">
                    <data-table
                        :items="productTableData.items.value"
                        :headers="productTableData.headers"
                        row-clickable
                        :loading="products_loading"
                        @click:row="onEditProductClick"
                    >
                        <template v-slot:item.type="{ item }">
                            {{ translateCalculateByType(item) }}
                        </template>
                        <template v-slot:item.article_id="{ item }">
                            {{ findProductFieldLabel('label', 'article_id', 'article', item) }}
                        </template>
                        <template v-slot:item.resource_id="{ item }">
                            {{ findProductFieldLabel('label', 'resource_id', 'resource', item) }}
                        </template>
                        <template v-slot:item.billing_project_id="{ item }">
                            {{ findProductFieldLabel('label', 'billing_project_id', 'billing_project', item) }}
                        </template>
                        <template v-slot:item.cost_center_id="{ item }">
                            {{ findProductFieldLabel('label', 'cost_center_id', 'cost_center', item) }}
                        </template>
                    </data-table>
                </v-col>
            </v-row>
            <v-row class="mb-5 ml-0">
                <secondary-button color="primary" @click="viewArchiveProducts">
                    {{ $t('View archive') }}
                </secondary-button>
            </v-row>

            <!-- ACCORDION -->
            <v-row no-gutters class="pl-4">
                <v-expansion-panels v-model="currentlyOpenIndex" class="mb-6">
                    <template v-for="(item, index) in productFieldTables">
                        <v-expansion-panel :key="item.key" class="mb-3 elevation-0 custom-expansion-shadow">

                            <v-expansion-panel-header disable-icon-rotate style="height:48px" class="elevation-0">
                                <v-row no-gutters align="center">
                                    <v-col class="shrink">
                                        <v-icon>{{ item.icon }}</v-icon>
                                    </v-col>
                                    <v-col style="max-width:2rem" class="text-right mr-2 font-weight-bold">
                                        {{
                                            item.type === 'tax'
                                                ? taxes.length
                                                : item.items.value.length
                                        }}
                                    </v-col>
                                    <v-col>
                                        {{
                                            item.type === 'tax'
                                                ? (taxes.length > 1 ? item.text_plural : item.text_singular)
                                                : (item.items.value.length > 1 ? item.text_plural : item.text_singular)
                                        }}
                                    </v-col>
                                </v-row>

                                <template v-slot:actions>
                                    <v-row>
                                        <v-col cols="12" :class="currentlyOpenIndex === index ? 'pr-0' : ''">

                                            <v-row no-gutters align="center" justify="space-between">
                                                <!-- OPEN -->
                                                <v-col cols="12" v-if="currentlyOpenIndex === index">
                                                    <v-row no-gutters class="flex-nowrap" align="center">
                                                        <v-col class="shrink">
                                                            <div class="ml-3">
                                                                <action-button
                                                                    @click.stop.prevent="onItemClick({
                                                                        type: item.type,
                                                                        text_plural: item.text_plural,
                                                                        text_singular: item.text_singular})">
                                                                    {{ $t('Add') }}
                                                                </action-button>
                                                            </div>
                                                        </v-col>
                                                    </v-row>
                                                </v-col>
                                                <!-- CLOSED -->
                                                <v-col cols="12" v-else>
                                                    <v-row no-gutters>
                                                        <v-col class="primary--text font-weight-medium">{{ $t('View').toUpperCase() }}</v-col>
                                                    </v-row>
                                                </v-col>
                                            </v-row>

                                        </v-col>
                                    </v-row>
                                </template>
                            </v-expansion-panel-header>

                            <v-expansion-panel-content>
                                <v-row no-gutters>
                                    <v-col cols="12">
                                        <data-table
                                            :headers="item.headers"
                                            :items="item.type === 'tax' ? taxes : item.items.value"
                                            row-clickable
                                            @click:row="onItemClick"
                                        >
                                        </data-table>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>

                        </v-expansion-panel>
                    </template>
                </v-expansion-panels>
            </v-row>
        </v-col>

        <EditCompanyProductDialog
            v-if="isProductDialogVisible"
            :company-id="accountId"
            :product-id="editingProductId"
            @close_modal="isProductDialogVisible = false"
        />

        <TaxDialog
            v-if="isTaxDialogVisible"
            :company-id="accountId"
            :tax-id="editingTaxId"
            :tax="selectedTax"
            @close="onTaxClose"
        />

    </v-row>
</template>

<script>
import {$t} from "@/i18n";
import {computed, onMounted, reactive, ref} from "@vue/composition-api";
import {translateCalculateByType} from "@/helpers/helpers"
import ProductFieldModule from "@/store/modules/product_field";
import ProductModule from "@/store/modules/product";
import BillingDialogContentHeader from "@/components/AppStore/BillingDialogContentHeader";
import AddButton from "@/components/Buttons/AddButton";
import DownloadButton from "@/components/Buttons/DownloadButton";
import FeatureModule from "@/store/modules/feature";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import ApiBillingProducts from "@/api/billing/products";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import EditCompanyProductDialog from "@/views/Dialog/EditProductDialog/EditCompanyProductDialog.vue";
import {formatPercent} from "../../../plugins/formatting";

import TemplateChooseDialog from "../../../components/Emailing/EmailEdit/TemplateChooseDialog.vue";
import AppStoreListHeader from "../../../components/AppStore/AppStoreListHeader.vue";
import ApiTax from "../../../api/billing/tax";
import {deepClone} from "../../../helpers/helpers";
import TaxDialog from "../../../components/Dialog/TaxDialog.vue";
import DialogModule from "../../../store/modules/dialog";


export default {
    name: "BillingDialogProductsTab",
    methods: {formatPercent},
    components: {
        AppStoreListHeader,
        TemplateChooseDialog,
        BillingDialogContentHeader,
        AddButton,
        DownloadButton,
        EditCompanyProductDialog,
        TaxDialog,
    },
    setup(props, ctx) {
        const {appStoreDialogGetters} = appStoreDialog()
        const {fetchProducts, products, products_loading} = ProductModule();
        const {fetchAccountProductFields, product_fields_by_type} = ProductFieldModule();
        const {openDialog, setDialogOptions, setConfirmOptions, openConfirmDialog} = DialogModule();
        const {execAppAction} = FeatureModule();

        const taxes = ref([])
        const editingTaxId = ref(null)
        const isTaxDialogVisible = ref(false)

        const fetchData = async function () {
            try {
                taxes.value = await ApiTax.getTaxes(selectedAccount.value.id)
            } catch (e) {
                errorDialog.showError(e)
            }
        }
        const selectedTax = computed(() => {
            let tax = taxes.value.find(x => x.id === editingTaxId.value)
            if (!tax) return null
            tax = deepClone(tax)
            return tax
        })
        const onTaxClose = async () => {
            editingTaxId.value = null
            isTaxDialogVisible.value = false
            await fetchData()
        }

        const editingProductId = ref(null)
        const isProductDialogVisible = ref(false)

        const import_loading = ref(false)
        const accountFeature = appStoreDialogGetters.getCurrentFeature()
        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        })

        const accountId = computed(() => {
            return selectedAccount.value.id
        })

        const currentlyOpenIndex = ref(-1)

        const findProductFieldLabel = (value_field, id_field, name_field, item) => {
            let obj_id = item && id_field in item ? item[id_field] : ''
            if (!obj_id) return ''
            const items = product_fields_by_type(name_field) ? product_fields_by_type(name_field).value : null
            const obj = items ? items.find(x => x.id === obj_id) : null
            return obj && value_field in obj ? obj[value_field] : ''
        }

        const productTableData = {
            text_plural: $t("products"),
            text_singular: $t("product"),
            account_id: accountId.value,
            headers: [
                {text: $t("Product / service"), value: "name"},
                {text: $t("Calculate"), value: "type"},
                {text: $t("Tax"), value: "tax"},
                {text: $t("Article"), value: "article_id"},
                {text: $t("Resource"), value: "resource_id"},
                {text: $t("Project"), value: "billing_project_id"},
                // {text: $t('Cost center'), value: "cost_center_id"},
                {value: "edit"}
            ],
            icon: "mdi-view-list",
            items: products,
            account: selectedAccount,
        }

        const productFieldTables = [
            {
                type: "tax",
                text_plural: $t("Taxes"),
                text_singular: $t("Tax"),
                account_id: accountId.value,
                headers: [
                    {text: $t("Percent"), value: "percent", sortable: true, width: '1%', align: 'right'},
                    {text: $t("Description"), value: "description", sortable: true, width: ''},
                    {text: $t("Code"), value: "ext_code", width: ''},
                    {text: $t("Source"), value: "source_text", sortable: true, width: ''},
                    // {text: $t("Visible"), value: "visible_text", sortable: true, width: '1%'},
                    {text: $t("Status"), value: "status_text", sortable: true, width: '1%'},
                    {value: "edit"}
                ],
                icon: "work",
                items: null,
                count: taxes ? taxes.length : 0,
                account: selectedAccount.value
            },
            {
                type: "article",
                text_plural: $t("Articles"),
                text_singular: $t("Article"),
                account_id: accountId.value,
                headers: [
                    {text: $t("Description"), value: "label"},
                    {text: $t("Code"), value: "code", width: '1%'},
                    {text: $t("Status"), value: "status_text", sortable: true, width: '1%'},
                    {value: "edit"}
                ],
                icon: "mdi-view-list",
                items: product_fields_by_type("article"),
                account: selectedAccount
            },
            {
                type: "resource",
                text_plural: $t("Resources"),
                text_singular: $t("Resource"),
                account_id: accountId.value,
                headers: [
                    {text: $t("Description"), value: "label"},
                    {text: $t("Code"), value: "code", width: '1%'},
                    {text: $t("Status"), value: "status_text", sortable: true, width: '1%'},
                    {value: "edit"}
                ],
                icon: "mdi-vector-difference",
                items: product_fields_by_type("resource"),
                account: selectedAccount.value
            },
            {
                type: "billing_project",
                text_plural: $t("Projects"),
                text_singular: $t("Project"),
                account_id: accountId.value,
                headers: [
                    {text: $t("Description"), value: "label"},
                    {text: $t("Code"), value: "code", width: '1%'},
                    {text: $t("Status"), value: "status_text", sortable: true, width: '1%'},
                    {value: "edit"}
                ],
                icon: "mdi-vector-difference",
                items: product_fields_by_type("billing_project"),
                account: selectedAccount.value
            },
            {
                type: "cost_center",
                text_plural: $t("Cost centers"),
                text_singular: $t("Cost center"),
                account_id: accountId.value,
                headers: [
                    {text: $t("Description"), value: "label"},
                    {text: $t("Code"), value: "code", width: '1%'},
                    {text: $t("Status"), value: "status_text", sortable: true, width: '1%'},
                    {value: "edit"}
                ],
                icon: "mdi-vector-difference",
                items: product_fields_by_type("cost_center"),
                account: selectedAccount.value
            },
            {
                type: "unit",
                text_plural: $t("Units"),
                text_singular: $t("Unit"),
                account_id: accountId.value,
                headers: [
                    {text: $t("Description"), value: "label"},
                    {text: $t("Code"), value: "code", width: '1%'},
                    {text: $t("Status"), value: "status_text", sortable: true, width: '1%'},
                    {value: "edit"}
                ],
                icon: "mdi-archive",
                items: product_fields_by_type("unit"),
                account: selectedAccount.value
            },

            // not integrations
            {
                type: "bank_account",
                text_plural: $t("Bank accounts"),
                text_singular: $t("Bank account"),
                account_id: accountId.value,
                headers: [
                    {text: $t("Bank name"), value: "label"},
                    {text: $t("IBAN"), value: "_data.iban"},
                    {text: $t("SWIFT code"), value: "_data.swift_code"},
                    {text: $t("Status"), value: "status_text", sortable: true, width: '1%'},
                    {value: "edit"}
                ],
                icon: "mdi-bank",
                items: product_fields_by_type("bank_account"),
                account: selectedAccount.value
            },
        ]

        const onItemClick = item => {
            if (!item || item.type === 'tax') {
                editingTaxId.value = item && item.id ? item.id : 0
                isTaxDialogVisible.value = true
            } else {
                setDialogOptions({
                    component: () => import("@/views/Dialog/EditProductFieldDialog"),
                    payload: {
                        account_id: accountId.value,
                        ...item
                    }
                })
                openDialog()
            }
        };

        const onAddProductClick = () => {
            editingProductId.value = null
            isProductDialogVisible.value = true
        };
        const onEditProductClick = product => {
            editingProductId.value = product.id
            isProductDialogVisible.value = true
        };

        const onImportProductClick = async () => {
            setConfirmOptions({
                title: $t("Do you wish to import only new data or also overwrite existing?"),
                confirm_text: $t("Only new"),
                action_tooltip: $t("New items will be added. Existing ones will remain unchanged by import."),
                confirm_text2: $t("Import everything"),
                cancel_text: $t("Cancel"),
                action: () => onImportProductClickConfirmed(true),
                action2: () => onImportProductClickConfirmed(false)
            });
            openConfirmDialog()
        }

        const onImportProductClickConfirmed = async (skip_if_exists) => {
            import_loading.value = true;
            try {
                await execAppAction({
                    account_id: selectedAccount.value.id,
                    feature_id: accountFeature.feature_id,
                    action: "import_product_data",
                    data: {skip_if_exists: skip_if_exists}
                })

                await fetchProducts(accountId.value)
                await fetchAccountProductFields(accountId.value)
                await fetchData()

            } catch (e) {
                errorDialog.showError(e)
            } finally {
                import_loading.value = false;
            }
        }
        const viewArchiveProducts = async () => {
            try {
                const data = await ApiBillingProducts.getArchivedProducts(accountId.value)
                const archivedProducts = data.products

                setConfirmOptions({
                    title: $t("Restore from archive"),
                    options: archivedProducts.map(function (x) {
                        return {name: x.id, text: x.name, checked: false};
                    }),
                    text: (archivedProducts.length < 1 && $t("Empty list")) || null,
                    confirm_text: $t("Restore"),
                    cancel_text: $t("Cancel"),
                    action: async (selected) => {
                        if (selected.length) {
                            await ApiBillingProducts.restoreArchivedProducts(accountId.value, selected)
                            await fetchProducts(accountId.value)
                        }
                    }
                });
                openConfirmDialog();
            } catch (ex) {
                errorDialog.showError(ex)
            }
        };

        onMounted(async () => {
            fetchProducts(accountId.value)
            fetchAccountProductFields(accountId.value)
            await fetchData()
        })

        return {
            accountId,
            currentlyOpenIndex,

            products_loading,
            productFieldTables,
            translateCalculateByType,
            onItemClick,

            products,
            productTableData,
            editingProductId,
            import_loading,
            onAddProductClick,
            onEditProductClick,
            onImportProductClick,
            viewArchiveProducts,
            findProductFieldLabel,
            isProductDialogVisible,

            taxes,
            editingTaxId,
            selectedTax,
            onTaxClose,
            isTaxDialogVisible,
        };
    }
};
</script>
